import { Link } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck } from "lucide-react";
import { CONTACT } from "@/data/siteData";
import SEO from "@/components/SEO";

const SECTIONS = [
  {
    h: "1. Titolare del trattamento",
    p: (
      <>
        Il Titolare del trattamento dei dati personali è il{" "}
        <strong>Dott. Cristian Santus</strong>, Medico Chirurgo Specialista in
        Gastroenterologia, con studi in Via Giulio Cesare 63, Portoscuso (SU) e
        Via Guido Cavalcanti 11 — Centro Polispecialistico San Benedetto,
        Cagliari.
        <br />
        Per qualsiasi richiesta relativa al trattamento dei dati personali è
        possibile scrivere a{" "}
        <a
          href={`mailto:${CONTACT.email}`}
          className="text-[#0B4A5A] font-medium link-underline"
        >
          {CONTACT.email}
        </a>
        .
      </>
    ),
  },
  {
    h: "2. Tipologia di dati raccolti",
    p: (
      <>
        Attraverso i moduli di contatto e i canali di lead generation presenti
        sul sito potranno essere raccolti i seguenti dati personali:
        <ul className="mt-4 space-y-2 list-disc list-inside">
          <li>Dati anagrafici (nome, cognome)</li>
          <li>Dati di contatto (indirizzo email, numero di telefono)</li>
          <li>
            Contenuto del messaggio e informazioni volontariamente comunicate
            (motivo della richiesta, sintomi riferiti, eventuali riferimenti a
            patologie o esami)
          </li>
        </ul>
        <p className="mt-4">
          Alcune delle informazioni comunicate possono rientrare, ai sensi
          dell'art. 9 GDPR, tra le <em>categorie particolari di dati personali</em>
          (dati relativi alla salute).
        </p>
      </>
    ),
  },
  {
    h: "3. Finalità del trattamento",
    p: (
      <>
        I dati personali forniti tramite i moduli di contatto o i canali di
        prenotazione verranno utilizzati <strong>esclusivamente</strong> per:
        <ul className="mt-4 space-y-2 list-disc list-inside">
          <li>gestire le richieste di prenotazione delle visite specialistiche;</li>
          <li>ricontattare l'utente al fine di rispondere alle sue domande;</li>
          <li>
            fornire informazioni sulle prestazioni erogate (visite, second
            opinion, follow-up, consulenze);
          </li>
          <li>
            adempiere agli obblighi di legge derivanti dall'esercizio della
            professione medica.
          </li>
        </ul>
        <p className="mt-4">
          I dati <strong>non</strong> vengono utilizzati per finalità di
          marketing, profilazione o comunicazione a terzi a scopo commerciale.
        </p>
      </>
    ),
  },
  {
    h: "4. Base giuridica del trattamento",
    p: (
      <>
        Il trattamento dei dati si fonda su:
        <ul className="mt-4 space-y-2 list-disc list-inside">
          <li>
            il <strong>consenso</strong> esplicito dell'interessato (art. 6,
            lett. a e art. 9, par. 2, lett. a GDPR);
          </li>
          <li>
            l'<strong>esecuzione di misure precontrattuali</strong> richieste
            dall'interessato (art. 6, lett. b GDPR);
          </li>
          <li>
            le finalità di <strong>medicina preventiva</strong>, diagnosi,
            assistenza o terapia sanitaria (art. 9, par. 2, lett. h GDPR).
          </li>
        </ul>
      </>
    ),
  },
  {
    h: "5. Trattamento dei dati relativi alla salute",
    p: (
      <>
        I dati relativi alla salute sono trattati nel pieno rispetto del{" "}
        <strong>Regolamento Europeo GDPR (UE 2016/679)</strong>, del D.lgs.
        196/2003 e successive modificazioni, e delle regole deontologiche
        dell'Ordine dei Medici.
        <br />
        <br />
        Tali dati sono coperti dal <strong>segreto professionale</strong> e
        vengono trattati con misure tecniche e organizzative adeguate a
        garantirne la riservatezza, l'integrità e la disponibilità.
      </>
    ),
  },
  {
    h: "6. Modalità e luogo di trattamento",
    p: (
      <>
        Il trattamento è effettuato con strumenti manuali ed elettronici, con
        modalità organizzative e con logiche strettamente correlate alle
        finalità indicate. I dati sono conservati in ambienti protetti, con
        accesso limitato al solo Titolare o a soggetti espressamente
        autorizzati (es. collaboratori sanitari o amministrativi vincolati da
        obbligo di riservatezza).
        <br />
        <br />I dati non vengono trasferiti al di fuori dell'Unione Europea.
      </>
    ),
  },
  {
    h: "7. Periodo di conservazione",
    p: (
      <>
        I dati raccolti tramite moduli di contatto sono conservati per il tempo
        strettamente necessario a evadere la richiesta e, comunque, non oltre{" "}
        <strong>24 mesi</strong> dall'ultimo contatto, salvo diverse esigenze
        di legge.
        <br />
        <br />I dati sanitari inerenti la relazione medico-paziente sono
        conservati secondo i termini di legge previsti per la documentazione
        clinica (di norma <strong>10 anni</strong>).
      </>
    ),
  },
  {
    h: "8. Cookie e strumenti di analisi",
    p: (
      <>
        Il sito utilizza cookie tecnici essenziali per il funzionamento e, previo
        consenso dell'utente, cookie analitici anonimi (Google Analytics 4) per
        misurare in forma aggregata il traffico e migliorare l'esperienza di
        navigazione. Non vengono utilizzati cookie di profilazione o pubblicitari.
        <br />
        <br />
        L'utente può in ogni momento modificare le proprie preferenze tramite il
        banner cookie visualizzato al primo accesso.
      </>
    ),
  },
  {
    h: "9. Diritti dell'interessato",
    p: (
      <>
        In qualsiasi momento l'interessato può esercitare, ai sensi degli
        articoli 15–22 del GDPR, i seguenti diritti:
        <ul className="mt-4 space-y-2 list-disc list-inside">
          <li>diritto di accesso ai propri dati personali;</li>
          <li>diritto di rettifica o integrazione dei dati inesatti;</li>
          <li>
            diritto alla cancellazione (<em>diritto all'oblio</em>);
          </li>
          <li>diritto alla limitazione del trattamento;</li>
          <li>diritto alla portabilità dei dati;</li>
          <li>diritto di opposizione al trattamento;</li>
          <li>diritto di revocare il consenso in qualsiasi momento.</li>
        </ul>
        <p className="mt-6">
          Per esercitare tali diritti — o per richiedere la modifica o la
          cancellazione dei propri dati — è sufficiente inviare una richiesta
          all'indirizzo{" "}
          <a
            href={`mailto:${CONTACT.email}`}
            className="text-[#0B4A5A] font-medium link-underline"
          >
            {CONTACT.email}
          </a>
          .
        </p>
      </>
    ),
  },
  {
    h: "10. Diritto di reclamo",
    p: (
      <>
        L'interessato ha diritto di proporre reclamo all'Autorità Garante per la
        Protezione dei Dati Personali (
        <a
          href="https://www.garanteprivacy.it"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#0B4A5A] font-medium link-underline"
        >
          www.garanteprivacy.it
        </a>
        ) qualora ritenga che il trattamento dei propri dati personali violi il
        Regolamento GDPR o la normativa italiana in materia di protezione dei
        dati.
      </>
    ),
  },
  {
    h: "11. Modifiche all'informativa",
    p: (
      <>
        La presente informativa può essere aggiornata in qualsiasi momento per
        adeguarla a modifiche normative o organizzative. La versione più recente
        è sempre pubblicata su questa pagina.
      </>
    ),
  },
];

export default function Privacy() {
  const lastUpdate = "Dicembre 2025";
  return (
    <main data-testid="privacy-page" className="pt-28">
      <SEO
        title="Informativa sulla Privacy e Trattamento dei Dati Personali"
        description="Informativa sulla privacy dello studio del Dr. Cristian Santus, Gastroenterologo. Trattamento dei dati personali e sanitari secondo GDPR (UE 2016/679)."
        path="/privacy"
      />

      <section className="relative py-16 md:py-24 bg-[#F9FAFB]">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#0B4A5A] transition"
          >
            <ArrowLeft size={14} /> Torna alla home
          </Link>
          <div className="flex items-center gap-2 mt-8">
            <ShieldCheck size={16} className="text-[#C6A86B]" strokeWidth={1.6} />
            <p className="overline">Privacy</p>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl mt-4 text-[#0B4A5A] leading-[1.05]">
            Informativa sulla Privacy e Trattamento dei Dati Personali
          </h1>
          <p className="mt-6 text-gray-600 leading-relaxed">
            Ai sensi del Regolamento Europeo (UE) 2016/679 — GDPR — e della
            normativa italiana vigente.
          </p>
          <p className="mt-3 text-sm text-gray-400">
            Ultimo aggiornamento: {lastUpdate}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 space-y-14">
          {SECTIONS.map((s) => (
            <div key={s.h}>
              <h2 className="font-serif text-2xl md:text-3xl text-[#0B4A5A]">
                {s.h}
              </h2>
              <div className="gold-line mt-4 w-12" />
              <div className="mt-5 text-gray-700 leading-[1.85]">{s.p}</div>
            </div>
          ))}

          <div className="mt-4 p-8 md:p-10 rounded-2xl bg-[#F9FAFB] border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C6A86B]/15 text-[#C6A86B] flex items-center justify-center">
                <Mail size={17} strokeWidth={1.6} />
              </div>
              <div>
                <p className="overline">Contatti privacy</p>
                <p className="font-serif text-lg text-[#0B4A5A] mt-0.5">
                  Dott. Cristian Santus
                </p>
              </div>
            </div>
            <p className="mt-5 text-gray-600 leading-relaxed">
              Per esercitare i tuoi diritti o richiedere informazioni sul
              trattamento dei tuoi dati, scrivi a:
            </p>
            <a
              href={`mailto:${CONTACT.email}`}
              className="inline-flex items-center gap-2 mt-4 text-[#0B4A5A] font-medium link-underline"
            >
              <Mail size={14} strokeWidth={1.6} />
              {CONTACT.email}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
